import { React, useState, useEffect } from 'react';
import axios from 'axios';
import '../../App.css'
import { Grid, TextField, Box, Button, Paper, Stack, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';

const initialRowValues = {
    id: 0,
    standardDeduction: '',
    cessRate: '',
    rebate: '',
}

const RequiredLabel = ({ label }) => (
    <span>
        {label}
        <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
    </span>
);

// Generate financial year options from 2020-2021 to 2029-2030
const generateFinancialYears = () => {
    const years = [];
    for (let startYear = 2020; startYear <= 2029; startYear++) {
        years.push(`${startYear}-${startYear + 1}`);
    }
    return years;
};

const financialYears = generateFinancialYears();

function AddIncomeTaxRulesForm(props) {
    const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
    const navigate = useNavigate();

    const [rows, setRows] = useState([{ ...initialRowValues }]);
    const [errors, setErrors] = useState({});
    const [financialYear, setFinancialYear] = useState("2025-2026"); // Hard coded financial year

    useEffect(() => {
        if (notify.isOpen && notify.type === 'success') {
          // Close the modal after a short delay to show the notification
          const timer = setTimeout(() => {
            // Call the parent's function to close the modal
            if (props.onCloseModal) {
              props.onCloseModal();
            }
          }, 100);
          
          return () => clearTimeout(timer);
        }
      }, [notify, props.onCloseModal]);

    useEffect(() => {
        async function getIncomeTaxRules() {
            if (props.fetch_id) {
                try {
                    // Fetch income tax rules for the financial year and find the matching one
                    const currentFinancialYear = financialYear || "2025-2026";
                    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/configure/tax-deductions/get/${encodeURIComponent(currentFinancialYear)}`);
                    if (response.status === 200 && response.data && response.data.data) {
                        // Find the rule with matching id
                        const fetchIdNum = Number(props.fetch_id);
                        const ruleData = Array.isArray(response.data.data) 
                            ? response.data.data.find(item => {
                                const itemId = Number(item.id || item.income_tax_rule_id);
                                return itemId === fetchIdNum || item.id === props.fetch_id || item.income_tax_rule_id === props.fetch_id;
                            })
                            : response.data.data;
                        
                        if (ruleData) {
                            const standardDeduction = ruleData.standard_deduction !== null && ruleData.standard_deduction !== undefined 
                                ? String(ruleData.standard_deduction) 
                                : (ruleData.standardDeduction || ruleData.StandardDeduction || '');
                            const cessRate = ruleData.cess_rate !== null && ruleData.cess_rate !== undefined 
                                ? String(ruleData.cess_rate) 
                                : (ruleData.cessRate || ruleData.CESSRate || ruleData.cess || '');
                            const rebate = ruleData.rebate !== null && ruleData.rebate !== undefined 
                                ? String(ruleData.rebate) 
                                : (ruleData.Rebate || '');
                            
                            setRows([{
                                id: props.fetch_id,
                                standardDeduction: standardDeduction,
                                cessRate: cessRate,
                                rebate: rebate,
                            }]);
                            
                            // Handle financial year
                            const effectiveDateValue = ruleData.financial_year || ruleData.effectiveDate || ruleData.effective_date || ruleData.EffectiveDate || '';
                            if (effectiveDateValue) {
                                // If it's a date string, convert to financial year format
                                if (effectiveDateValue.includes('-') && effectiveDateValue.length === 10) {
                                    const date = new Date(effectiveDateValue);
                                    const year = date.getFullYear();
                                    const month = date.getMonth();
                                    if (month >= 3) {
                                        setFinancialYear(`${year}-${year + 1}`);
                                    } else {
                                        setFinancialYear(`${year - 1}-${year}`);
                                    }
                                } else {
                                    // Assume it's already in financial year format
                                    setFinancialYear(effectiveDateValue);
                                }
                            } else {
                                setFinancialYear("2025-2026");
                            }
                        } else {
                            // If not found, show error
                            setNotify({
                                isOpen: true,
                                message: 'Income Tax Rule not found',
                                type: 'error'
                            });
                        }
                    }
                } catch (e) {
                    if (e.response && e.response.status === 401) {
                        setNotify({
                            isOpen: true,
                            message: 'Session Expired!',
                            type: 'error'
                        });
                        setTimeout(() => {
                            navigate('/');
                        }, 1000);
                    } else {
                        setNotify({
                            isOpen: true,
                            message: 'Failed to load income tax rule data',
                            type: 'error'
                        });
                    }
                }
            } else {
                // Reset to single row when adding new
                setRows([{ ...initialRowValues }]);
                setFinancialYear("2025-2026");
            }
        }
        getIncomeTaxRules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.fetch_id]);

    const validate = () => {
        let temp = {}
        let isValid = true;

        rows.forEach((row, index) => {
            const rowPrefix = `row_${index}_`;
            
            // Validate standard deduction
            if (row.standardDeduction === '' || row.standardDeduction === null || row.standardDeduction === undefined) {
                temp[`${rowPrefix}standardDeduction`] = "This field is required.";
                isValid = false;
            } else {
                const standardDeductionNum = parseFloat(row.standardDeduction);
                if (isNaN(standardDeductionNum) || standardDeductionNum < 0) {
                    temp[`${rowPrefix}standardDeduction`] = "Standard deduction cannot be negative.";
                    isValid = false;
                }
            }

            // Validate CESS rate
            if (!row.cessRate) {
                temp[`${rowPrefix}cessRate`] = "This field is required.";
                isValid = false;
            } else {
                const cessRateNum = parseFloat(row.cessRate);
                if (isNaN(cessRateNum) || cessRateNum < 0 || cessRateNum > 100) {
                    temp[`${rowPrefix}cessRate`] = "CESS rate must be a number between 0 and 100.";
                    isValid = false;
                }
            }

            // Validate rebate
            if (row.rebate === '' || row.rebate === null || row.rebate === undefined) {
                temp[`${rowPrefix}rebate`] = "This field is required.";
                isValid = false;
            } else {
                const rebateNum = parseFloat(row.rebate);
                if (isNaN(rebateNum) || rebateNum < 0) {
                    temp[`${rowPrefix}rebate`] = "Rebate cannot be negative.";
                    isValid = false;
                }
            }
        });

        // Validate financial year
        if (!financialYear) {
            temp.financialYear = "This field is required.";
            isValid = false;
        }

        setErrors(temp);
        return isValid;
    }

    const resetForm = () => {
        setRows([{ ...initialRowValues }]);
        setFinancialYear("2025-2026");
        setErrors({});
    }

    function handleInputChange(e, index) {
        const { name, value } = e.target;
        const newRows = [...rows];
        newRows[index] = {
            ...newRows[index],
            [name]: value
        };
        setRows(newRows);
        
        // Clear error for this field
        const errorKey = `row_${index}_${name}`;
        const newErrors = { ...errors };
        if (newErrors[errorKey]) {
            delete newErrors[errorKey];
        }
        setErrors(newErrors);
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            if (props.fetch_id) {
                // Edit existing Income Tax Rule
                const row = rows[0];
                const payload = {
                    standard_deduction: parseFloat(row.standardDeduction),
                    cess_rate: parseFloat(row.cessRate),
                    rebate: parseFloat(row.rebate),
                    financial_year: financialYear,
                    id: props.fetch_id,
                };

                axios.post(`${process.env.REACT_APP_API_BASE_URL}/configure/tax-deductions/update`, payload)
                    .then(function (response) {
                        if (response.status === 200) {
                            // Trigger refresh callback immediately
                            if (props.onRefresh) {
                                props.onRefresh();
                            }
                            // Show success notification
                            setNotify({
                                isOpen: true,
                                message: response.data.message || 'Income Tax Rule updated successfully',
                                type: 'success'
                            });
                            // Close modal immediately
                            if (props.setOpenPopup) {
                                props.setOpenPopup(false);
                            }
                            if (props.onCloseModal) {
                                props.onCloseModal();
                            }
                        }
                    })
                    .catch(function (e) {
                        handleApiError(e);
                    });
            } else {
                // Add new Income Tax Rule
                const row = rows[0];
                const payload = {
                    standard_deduction: parseFloat(row.standardDeduction),
                    cess_rate: parseFloat(row.cessRate),
                    rebate: parseFloat(row.rebate),
                    financial_year: financialYear,
                };

                axios.post(`${process.env.REACT_APP_API_BASE_URL}/configure/tax-deductions/add`, payload)
                    .then(function (response) {
                        if (response.status === 200 || response.status === 201) {
                            // Trigger refresh callback immediately
                            if (props.onRefresh) {
                                props.onRefresh();
                            }
                            // Reset form
                            resetForm();
                            // Show success notification
                            setNotify({
                                isOpen: true,
                                message: 'Income Tax Rule added successfully',
                                type: 'success'
                            });
                            // Close modal immediately
                            if (props.setOpenPopup) {
                                props.setOpenPopup(false);
                            }
                            if (props.onCloseModal) {
                                props.onCloseModal();
                            }
                        }
                    })
                    .catch(function (e) {
                        handleApiError(e);
                    });
            }
        }
    }

    const handleApiError = (e) => {
        if (e.response && e.response.status === 401) {
            setNotify({
                isOpen: true,
                message: 'Session Expired!',
                type: 'error'
            });
            setTimeout(() => {
                navigate('/');
            }, 1000);
        } else if (e.response && e.response.status === 422) {
            setNotify({
                isOpen: true,
                message: e.response.data.error_message?.sqlMessage || e.response.data.error_message || 'An error occurred',
                type: 'error'
            });
        } else {
            setNotify({
                isOpen: true,
                message: 'An error occurred. Please try again.',
                type: 'error'
            });
        }
    }

    return (
        <Box sx={{ display: "flex", justifyContent: "center", margin: "-10px", padding: "30px", fontWeight: "500px" }}>
            <Paper variant="outlined" elevation={5} sx={{ width: "700px", marginTop: "-20px" }}>
                <form className="classes.root" autoComplete="off" onSubmit={handleSubmit}>
                    <Stack spacing={2} sx={{ margin: "30px"}}>
                        {rows.map((row, index) => (
                            <Box key={index} sx={{ position: 'relative' }}>
                                {/* First Row: Standard Deduction, CESS Rate */}
                                <Grid container spacing={2} style={{ marginLeft: "25px", marginBottom: "15px" }} alignItems="flex-start">
                                    <Grid item xs={5.5}>
                                        <TextField
                                            variant="outlined"
                                            name="standardDeduction"
                                            label={<RequiredLabel label="Standard Deduction (&#8377;)" />}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            inputProps={{ min: "0", step: "0.01" }}
                                            value={row.standardDeduction}
                                            onChange={(e) => handleInputChange(e, index)}
                                            {...(errors[`row_${index}_standardDeduction`] && { 
                                                error: true, 
                                                helperText: errors[`row_${index}_standardDeduction`] 
                                            })}
                                        />
                                    </Grid>
                                    <Grid item xs={5.5}>
                                        <TextField
                                            variant="outlined"
                                            name="cessRate"
                                            label={<RequiredLabel label="CESS Rate (%)" />}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            inputProps={{ step: "0.01", min: "0", max: "100" }}
                                            value={row.cessRate}
                                            onChange={(e) => handleInputChange(e, index)}
                                            {...(errors[`row_${index}_cessRate`] && { 
                                                error: true, 
                                                helperText: errors[`row_${index}_cessRate`] 
                                            })}
                                        />
                                    </Grid>
                                </Grid>
                                {/* Second Row: Rebate, Financial Year */}
                                <Grid container spacing={2} style={{ marginLeft: "25px" }} alignItems="flex-start">
                                    <Grid item xs={5.5}>
                                        <TextField
                                            variant="outlined"
                                            name="rebate"
                                            label={<RequiredLabel label="Rebate (&#8377;)" />}
                                            size="small"
                                            type="number"
                                            fullWidth
                                            inputProps={{ min: "0", step: "0.01" }}
                                            value={row.rebate}
                                            onChange={(e) => handleInputChange(e, index)}
                                            {...(errors[`row_${index}_rebate`] && { 
                                                error: true, 
                                                helperText: errors[`row_${index}_rebate`] 
                                            })}
                                        />
                                    </Grid>
                                    <Grid item xs={5.5}>
                                        {index === 0 && (
                                            <FormControl 
                                                variant="outlined" 
                                                size="small" 
                                                fullWidth
                                                error={!!errors.financialYear}
                                            >
                                                <InputLabel id="financial-year-label">
                                                    Financial Year <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
                                                </InputLabel>
                                                <Select
                                                    labelId="financial-year-label"
                                                    value={financialYear}
                                                    onChange={(e) => {
                                                        setFinancialYear(e.target.value);
                                                        if (errors.financialYear) {
                                                            const newErrors = { ...errors };
                                                            delete newErrors.financialYear;
                                                            setErrors(newErrors);
                                                        }
                                                    }}
                                                    label="Financial Year *"
                                                >
                                                    {financialYears.map((year) => (
                                                        <MenuItem key={year} value={year}>
                                                            {year}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.financialYear && (
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                                        {errors.financialYear}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}

                        <Grid container className="space-around" sx={{ mt: 2 }} style={{ justifyContent: "center" , marginTop: "20px" }}>
                            <Grid item>
                                <Button variant="contained" color="error" onClick={resetForm}>Reset</Button>
                            </Grid>
                            <Grid item>
                                <Button style={{ marginLeft: "10px" }} variant="contained" type="submit" color="success">
                                    {props.fetch_id ? 'Update' : 'Submit'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Stack>
                </form>
            </Paper>
            <Notification
                notify={notify}
                setNotify={setNotify}
            />
        </Box>
    )
}

export default AddIncomeTaxRulesForm;

